import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
  flexRender,
} from "@tanstack/react-table";
import {
  BarChart2,
  TrendingUp,
  DollarSign,
  Package,
  Sparkles,
  Loader2,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, formatUsd } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface BrandSummary {
  brand: string;
  total_skus: number;
  in_stock_count: number;
  avg_margin_pct: number;
  avg_score: number;
  top_categories: string[];
}

interface CuratedItem {
  state: string;
  [key: string]: unknown;
}

interface ScoredSku {
  tdsynnex_sku: string;
  description: string;
  brand: string;
  score: number;
  margin_pct: number;
  cost: number;
  msrp: number;
  qty_on_hand: number;
  in_stock: boolean;
  curated_state: string | null;
  curated_id: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return formatUsd(n);
}

function scoreColor(score: number): string {
  if (score >= 0.8) return "text-emerald-500";
  if (score >= 0.6) return "text-lime-500";
  if (score >= 0.4) return "text-amber-500";
  if (score >= 0.2) return "text-orange-500";
  return "text-rose-500";
}

function scoreBarColor(score: number): string {
  if (score >= 0.8) return "bg-emerald-500";
  if (score >= 0.6) return "bg-lime-500";
  if (score >= 0.4) return "bg-amber-500";
  if (score >= 0.2) return "bg-orange-500";
  return "bg-rose-500";
}

function ScoreBar({ value }: { value: number }) {
  const pct = Math.min(100, Math.max(0, value * 100));
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-muted/40">
        <div
          className={cn("h-full rounded-full transition-all", scoreBarColor(value))}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn("tabular-nums text-xs font-mono w-9 text-right", scoreColor(value))}>
        {(pct).toFixed(0)}%
      </span>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon: Icon,
  label,
  value,
  subtitle,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtitle: string;
  accent?: string;
}) {
  return (
    <div className="bg-muted/20 border border-border/60 rounded-lg p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {label}
      </div>
      <div className={cn("text-3xl font-bold tabular-nums tracking-tight", accent ?? "text-foreground")}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
    </div>
  );
}

// ── Curation Funnel ───────────────────────────────────────────────────────────

const FUNNEL_STATES = [
  { state: "draft", label: "Draft", bar: "bg-slate-500", text: "text-slate-400" },
  { state: "ready", label: "Ready", bar: "bg-amber-500", text: "text-amber-400" },
  { state: "published", label: "Published", bar: "bg-emerald-500", text: "text-emerald-400" },
] as const;

function CurationFunnel({ curated }: { curated: CuratedItem[] }) {
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of curated) {
      map[item.state] = (map[item.state] ?? 0) + 1;
    }
    return map;
  }, [curated]);

  const total = curated.length;
  const max = Math.max(1, ...FUNNEL_STATES.map((s) => counts[s.state] ?? 0));

  return (
    <div className="bg-muted/20 border border-border/60 rounded-lg p-4 h-full flex flex-col gap-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        Curation Funnel
      </div>
      <div className="flex-1 flex flex-col justify-center gap-4">
        {FUNNEL_STATES.map(({ state, label, bar, text }) => {
          const count = counts[state] ?? 0;
          const widthPct = max > 0 ? (count / max) * 100 : 0;
          const pctOfTotal = total > 0 ? ((count / total) * 100).toFixed(1) : "0";
          return (
            <div key={state} className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs">
                <span className={cn("font-medium", text)}>{label}</span>
                <span className="tabular-nums text-muted-foreground font-mono">
                  {count.toLocaleString()}
                  <span className="text-muted-foreground/50 ml-1">({pctOfTotal}%)</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all duration-500", bar)}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Score Distribution ────────────────────────────────────────────────────────

const SCORE_BANDS = [
  { label: "0–20%", min: 0, max: 0.2, fill: "#f43f5e" },
  { label: "20–40%", min: 0.2, max: 0.4, fill: "#f97316" },
  { label: "40–60%", min: 0.4, max: 0.6, fill: "#f59e0b" },
  { label: "60–80%", min: 0.6, max: 0.8, fill: "#84cc16" },
  { label: "80–100%", min: 0.8, max: 1.01, fill: "#10b981" },
];

function ScoreDistribution({ skus }: { skus: ScoredSku[] }) {
  const buckets = useMemo(() => {
    return SCORE_BANDS.map((band) => ({
      ...band,
      count: skus.filter((s) => s.score >= band.min && s.score < band.max).length,
    }));
  }, [skus]);

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));

  const svgH = 140;
  const svgW = 500;
  const padL = 10;
  const padR = 10;
  const padTop = 28;
  const padBot = 28;
  const innerW = svgW - padL - padR;
  const innerH = svgH - padTop - padBot;
  const barW = innerW / buckets.length;
  const gap = 10;

  return (
    <div className="bg-muted/20 border border-border/60 rounded-lg p-4 h-full flex flex-col gap-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <BarChart2 className="h-4 w-4 text-muted-foreground" />
        Score Distribution
        <span className="ml-auto text-xs font-normal text-muted-foreground tabular-nums">
          {skus.length.toLocaleString()} SKUs
        </span>
      </div>
      <div className="flex-1 min-h-0">
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          {buckets.map((b, i) => {
            const barH = maxCount > 0 ? (b.count / maxCount) * innerH : 0;
            const x = padL + i * barW + gap / 2;
            const w = barW - gap;
            const y = padTop + innerH - barH;
            return (
              <g key={b.label}>
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={barH}
                  rx={3}
                  fill={b.fill}
                  opacity={0.85}
                />
                {/* count label */}
                <text
                  x={x + w / 2}
                  y={y - 6}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#94a3b8"
                  fontFamily="'JetBrains Mono', monospace"
                >
                  {b.count}
                </text>
                {/* x-axis label */}
                <text
                  x={x + w / 2}
                  y={svgH - 6}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#64748b"
                >
                  {b.label}
                </text>
              </g>
            );
          })}
          {/* baseline */}
          <line
            x1={padL}
            y1={padTop + innerH}
            x2={svgW - padR}
            y2={padTop + innerH}
            stroke="#334155"
            strokeWidth={1}
          />
        </svg>
      </div>
    </div>
  );
}

// ── Brand Scorecard ───────────────────────────────────────────────────────────

interface BrandRow extends BrandSummary {
  potential_revenue: number;
}

function BrandScorecard({
  brands,
  top500,
}: {
  brands: BrandSummary[];
  top500: ScoredSku[];
}) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "potential_revenue", desc: true },
  ]);

  const rows: BrandRow[] = useMemo(() => {
    const revenueByBrand: Record<string, number> = {};
    for (const sku of top500) {
      if (!sku.in_stock) continue;
      const rev = (sku.msrp - sku.cost) * sku.qty_on_hand;
      revenueByBrand[sku.brand] = (revenueByBrand[sku.brand] ?? 0) + Math.max(0, rev);
    }
    return brands.map((b) => ({
      ...b,
      potential_revenue: revenueByBrand[b.brand] ?? 0,
    }));
  }, [brands, top500]);

  const columns: ColumnDef<BrandRow>[] = useMemo(
    () => [
      {
        accessorKey: "brand",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Brand <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue }) => (
          <span className="font-medium text-foreground">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: "total_skus",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Total SKUs <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue }) => (
          <span className="tabular-nums font-mono text-sm">
            {(getValue() as number).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "in_stock_count",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            In Stock <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue, row }) => {
          const inStock = getValue() as number;
          const total = row.original.total_skus;
          const pct = total > 0 ? ((inStock / total) * 100).toFixed(0) : "0";
          return (
            <span className="tabular-nums font-mono text-sm">
              {inStock.toLocaleString()}
              <span className="text-muted-foreground text-xs ml-1">({pct}%)</span>
            </span>
          );
        },
      },
      {
        accessorKey: "avg_score",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Avg Score <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue }) => <ScoreBar value={getValue() as number} />,
      },
      {
        accessorKey: "avg_margin_pct",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Avg Margin% <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue }) => {
          const v = getValue() as number;
          return (
            <span
              className={cn(
                "tabular-nums font-mono text-sm",
                v >= 20 ? "text-emerald-500" : v >= 10 ? "text-amber-500" : "text-rose-500",
              )}
            >
              {v.toFixed(1)}%
            </span>
          );
        },
      },
      {
        id: "top_category",
        accessorFn: (row) => row.top_categories?.[0] ?? "—",
        header: "Top Category",
        cell: ({ getValue }) => (
          <Badge variant="secondary" className="text-xs font-normal">
            {getValue() as string}
          </Badge>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "potential_revenue",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-7 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Potential Rev <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ getValue }) => (
          <span className="tabular-nums font-mono text-sm text-emerald-400">
            {formatCompact(getValue() as number)}
          </span>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="bg-muted/20 border border-border/60 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
        <BarChart2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">Brand Scorecard</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {rows.length} brands
        </span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="border-border/40 hover:bg-transparent">
                {hg.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="bg-muted/10 text-muted-foreground text-xs h-9 whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="border-border/30 hover:bg-muted/10">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-2 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Top Opportunities ─────────────────────────────────────────────────────────

function TopOpportunities({
  top500,
  onDraftted,
}: {
  top500: ScoredSku[];
  onDraftted: () => void;
}) {
  const [drafting, setDrafting] = useState<Record<string, boolean>>({});

  const opps = useMemo(() => {
    return top500
      .filter((s) => s.in_stock && (!s.curated_state || s.curated_state === "uncurated"))
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
  }, [top500]);

  const handleDraft = useCallback(
    async (sku: string) => {
      setDrafting((prev) => ({ ...prev, [sku]: true }));
      try {
        const res = await fetch(`/api/curate/${sku}`, { method: "POST" });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        toast.success(`Drafted ${sku}`, { description: "Added to curation queue." });
        onDraftted();
      } catch (err) {
        toast.error("Draft failed", {
          description: err instanceof Error ? err.message : "Unknown error",
        });
      } finally {
        setDrafting((prev) => ({ ...prev, [sku]: false }));
      }
    },
    [onDraftted],
  );

  return (
    <div className="bg-muted/20 border border-border/60 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-400" />
        <span className="text-sm font-semibold text-foreground">Top Opportunities</span>
        <span className="text-xs text-muted-foreground ml-1">— money on the table</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          top {opps.length} uncurated / in-stock
        </span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border/40 hover:bg-transparent">
              {["SKU", "Brand", "Description", "Score", "Margin%", "Cost", "MSRP", "Qty", ""].map(
                (h, i) => (
                  <TableHead
                    key={i}
                    className="bg-muted/10 text-muted-foreground text-xs h-9 whitespace-nowrap"
                  >
                    {h}
                  </TableHead>
                ),
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {opps.map((sku) => (
              <TableRow key={sku.tdsynnex_sku} className="border-border/30 hover:bg-muted/10">
                <TableCell className="py-1.5">
                  <span className="font-mono text-xs text-muted-foreground">
                    {sku.tdsynnex_sku}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className="text-xs font-medium">{sku.brand}</span>
                </TableCell>
                <TableCell className="py-1.5 max-w-[240px]">
                  <span
                    className="text-xs text-muted-foreground truncate block"
                    title={sku.description}
                  >
                    {sku.description.length > 60
                      ? `${sku.description.slice(0, 59)}…`
                      : sku.description}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className={cn("tabular-nums font-mono text-xs font-semibold", scoreColor(sku.score))}>
                    {(sku.score * 100).toFixed(0)}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span
                    className={cn(
                      "tabular-nums font-mono text-xs",
                      sku.margin_pct >= 20
                        ? "text-emerald-500"
                        : sku.margin_pct >= 10
                          ? "text-amber-500"
                          : "text-rose-500",
                    )}
                  >
                    {sku.margin_pct.toFixed(1)}%
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className="tabular-nums font-mono text-xs text-muted-foreground">
                    {formatUsd(sku.cost)}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className="tabular-nums font-mono text-xs">
                    {formatUsd(sku.msrp)}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className="tabular-nums font-mono text-xs">{sku.qty_on_hand}</span>
                </TableCell>
                <TableCell className="py-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-xs px-2 border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300"
                    disabled={drafting[sku.tdsynnex_sku]}
                    onClick={() => handleDraft(sku.tdsynnex_sku)}
                  >
                    {drafting[sku.tdsynnex_sku] ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "Draft"
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {opps.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-muted-foreground text-sm">
                  No uncurated in-stock opportunities found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [curated, setCurated] = useState<CuratedItem[]>([]);
  const [top500, setTop500] = useState<ScoredSku[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [brandsRes, curatedRes, top500Res] = await Promise.all([
        fetch("/api/analytics/score/brands").then((r) => {
          if (!r.ok) throw new Error(`brands: ${r.status}`);
          return r.json() as Promise<BrandSummary[]>;
        }),
        fetch("/api/curated").then((r) => {
          if (!r.ok) throw new Error(`curated: ${r.status}`);
          return r.json() as Promise<CuratedItem[]>;
        }),
        fetch("/api/analytics/score/top?limit=500&preset=profitable").then((r) => {
          if (!r.ok) throw new Error(`top500: ${r.status}`);
          return r.json() as Promise<ScoredSku[]>;
        }),
      ]);
      setBrands(brandsRes ?? []);
      setCurated(curatedRes ?? []);
      setTop500(top500Res ?? []);
      setLastFetched(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── KPI computations ────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const totalBrands = brands.length;

    const potentialRevenue = top500
      .filter((s) => s.in_stock && (!s.curated_state || s.curated_state === "uncurated"))
      .reduce((acc, s) => acc + Math.max(0, (s.msrp - s.cost) * s.qty_on_hand), 0);

    const curatedCount = curated.filter(
      (c) => c.state !== "uncurated" && c.state !== "archived",
    ).length;

    const publishedCount = curated.filter((c) => c.state === "published").length;

    return { totalBrands, potentialRevenue, curatedCount, publishedCount };
  }, [brands, curated, top500]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border/60 px-6 py-3 flex items-center gap-3">
        <BarChart2 className="h-5 w-5 text-emerald-500" />
        <h1 className="text-base font-semibold text-foreground">Analytics</h1>
        {lastFetched && (
          <span className="text-xs text-muted-foreground ml-1">
            Updated {lastFetched.toLocaleTimeString()}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto gap-1.5 text-xs"
          disabled={loading}
          onClick={fetchAll}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Error banner */}
        {error && (
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 px-4 py-3 text-sm text-rose-400">
            {error}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !error && (
          <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading analytics…</span>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Section 1 — KPI Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiCard
                icon={Package}
                label="Catalog Brands"
                value={kpis.totalBrands}
                subtitle="TD SYNNEX active"
                accent="text-foreground"
              />
              <KpiCard
                icon={DollarSign}
                label="Potential Revenue"
                value={formatCompact(kpis.potentialRevenue)}
                subtitle="Uncurated, in-stock margin"
                accent="text-emerald-400"
              />
              <KpiCard
                icon={Sparkles}
                label="Curated"
                value={kpis.curatedCount.toLocaleString()}
                subtitle="Draft + ready + published"
                accent="text-amber-400"
              />
              <KpiCard
                icon={TrendingUp}
                label="Published"
                value={kpis.publishedCount.toLocaleString()}
                subtitle="Live on Shopify"
                accent="text-emerald-500"
              />
            </div>

            {/* Section 2 — Funnel + Distribution */}
            <div className="grid grid-cols-5 gap-4" style={{ minHeight: "200px" }}>
              <div className="col-span-2">
                <CurationFunnel curated={curated} />
              </div>
              <div className="col-span-3">
                <ScoreDistribution skus={top500} />
              </div>
            </div>

            {/* Section 3 — Brand Scorecard */}
            <BrandScorecard brands={brands} top500={top500} />

            {/* Section 4 — Top Opportunities */}
            <TopOpportunities top500={top500} onDraftted={fetchAll} />
          </>
        )}
      </div>
    </div>
  );
}
